import {KeyValue} from "../stores/observables/keyValue";
import {
  allVillageKeyValue,
  allVillagesInAU,
  allVillagesInNZ,
  independentResidents,
  independentAndServicedApartmentResidents,
  multiVillageKeyValue
} from "./constants";
import {personTypeIds} from "./personTypeIds";
import {IUiStore} from "../stores/uiStore/uiStore";
import {TFunction} from "react-i18next";
import {calculateHasSharedCalendar, Village} from "./interfaces/village";
import {TimeZones} from "./enums/timeZones";
import {CountryCodes} from "./enums/countryCodes";
import {EventCalendarType} from "./enums/eventCalendarType";

export type VillagesByCountrySummary = {
  allNZSelected: boolean;
  allAUSelected: boolean;
  allVillagesSelected: boolean;
  selectedVillages: Village[];
};

export type VillageMap = {
  [key: string]: Village[];
};

export class MultiVillageHelper {
  static VillageEllipsis(
    translationFunction: TFunction<string[]>,
    villageList: string[],
    isMultiVillageAdmin?: boolean,
    limit: number | null = 3
  ): string {
    if (!isMultiVillageAdmin) {
      return translationFunction("translation:multipleVillages");
    }

    return limit !== null && villageList.length > limit
      ? villageList.slice(0, limit).join(", ") +
          ` + ${translationFunction("translation:more", {
            count: villageList.length - limit
          })}`
      : villageList.join(", ");
  }

  static IsMultiVillageOptionPresent(villages: KeyValue[]): boolean {
    return villages.some((v) => v.value === multiVillageKeyValue.value);
  }

  /**
   * Show/hide by adding/removing Multi Village option in the village selector.
   * @param uiStore
   * @param shouldShow
   */
  static ShowOrHideMultiVillageOption(
    uiStore: IUiStore,
    shouldShow: boolean
  ): void {
    const selectableVillagesClone = [...uiStore.selectableVillages.items];
    if (
      this.IsMultiVillageOptionPresent(uiStore.selectableVillages.items) &&
      !shouldShow
    ) {
      selectableVillagesClone.shift();
      uiStore.selectableVillages.setItems(selectableVillagesClone);
    } else if (
      !this.IsMultiVillageOptionPresent(uiStore.selectableVillages.items) &&
      shouldShow
    ) {
      selectableVillagesClone.unshift(multiVillageKeyValue);
      uiStore.selectableVillages.setItems(selectableVillagesClone);
    }
  }

  static get multiVillageVillage(): Village {
    const calendars = [
      {
        personType: personTypeIds.independent,
        calendarType: EventCalendarType.Enhanced,
        name: independentResidents,
        calendarColor: "Purple"
      },
      {
        personType: personTypeIds.independent,
        personTypeIds: [personTypeIds.independent, personTypeIds.serviced],
        calendarType: EventCalendarType.Enhanced,
        name: independentAndServicedApartmentResidents,
        calendarColor: "Teal"
      }
    ];
    return {
      id: multiVillageKeyValue.value,
      name: multiVillageKeyValue.label,
      timeZone: TimeZones.Auckland,
      code: multiVillageKeyValue.value,
      calendars,
      hasSharedCalendar: calculateHasSharedCalendar(calendars)
    };
  }

  static multiVillageString(
    selectedVillages: string[],
    villagesByCountry: {[key: string]: Village[]}
  ): string {
    let villagesString = "";

    const numberOfNZVillages = villagesByCountry[CountryCodes.NZ].length;
    const numberOfAUVillages = villagesByCountry[CountryCodes.AU].length;

    if (selectedVillages.length === numberOfNZVillages + numberOfAUVillages) {
      if (
        MultiVillageHelper.everyVillageInCountrySelected(selectedVillages, [
          ...villagesByCountry[CountryCodes.NZ],
          ...villagesByCountry[CountryCodes.AU]
        ])
      ) {
        villagesString = allVillageKeyValue.label;
      }
    } else if (selectedVillages.length === numberOfAUVillages) {
      if (
        MultiVillageHelper.everyVillageInCountrySelected(
          selectedVillages,
          villagesByCountry[CountryCodes.AU]
        )
      ) {
        villagesString = allVillagesInAU.label;
      }
    } else if (selectedVillages.length === numberOfNZVillages) {
      if (
        MultiVillageHelper.everyVillageInCountrySelected(
          selectedVillages,
          villagesByCountry[CountryCodes.NZ]
        )
      ) {
        villagesString = allVillagesInNZ.label;
      }
    }

    return villagesString;
  }

  static everyVillageInCountrySelected(
    selectedVillages: string[],
    targetCountryVillages: Village[]
  ): Boolean {
    return (
      selectedVillages.length === targetCountryVillages.length &&
      targetCountryVillages.every((village) =>
        selectedVillages.includes(village.name)
      )
    );
  }

  // Fancy smancy stuff
  // This will collect villages by country, and summarize them.
  // It's not memory efficient, but it only uses 1 loop of the villages.
  static summariseSelectedVillages(
    selectedVillages: KeyValue[],
    villagesByCountry: VillageMap
  ): VillagesByCountrySummary {
    const allCountryCodes = Object.keys(villagesByCountry);

    // Convert to hash map, so we can quickly look up + remove villages
    const selectedVillageSet = new Set<string>(
      selectedVillages.map((kv) => kv.value)
    );

    // A list of villages per country that have been selected
    const foundVillages: VillageMap = {};
    // A list of villages per country that have not been selected
    const missingVillages: VillageMap = {};

    // Initialise the arrays to avoid null pointer exceptions
    allCountryCodes.forEach((countryCode) => {
      foundVillages[countryCode] = [];
      missingVillages[countryCode] = [];
    });

    // Find all villages that have not been selected
    allCountryCodes.forEach((countryCode: string) => {
      // Loop through all villages in the country
      villagesByCountry[countryCode].forEach((village) => {
        if (selectedVillageSet.has(village.id)) {
          foundVillages[countryCode].push(village);
        } else {
          missingVillages[countryCode].push(village);
        }
      });
    });

    const allNZSelected = missingVillages[CountryCodes.NZ].length === 0;
    const allAUSelected = missingVillages[CountryCodes.AU].length === 0;
    const allVillagesSelected = allNZSelected && allAUSelected;
    // Only return selected villages if they are not all selected for a country
    const remainingVillages = [
      ...(allNZSelected ? [] : foundVillages[CountryCodes.NZ]),
      ...(allAUSelected ? [] : foundVillages[CountryCodes.AU])
    ];
    return {
      allNZSelected,
      allAUSelected,
      allVillagesSelected,
      selectedVillages: remainingVillages
    };
  }
}
